import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight } from "lucide-react";

interface BlogPost {
  title: string;
  excerpt: string;
  date: string;
  author: string;
  category: string;
  slug: string;
  keywords: string[];
}

// SEO-rich blog posts about pharmaceutical APIs
const blogPosts: BlogPost[] = [
  {
    title: "Understanding FDA Compliance for Pharmaceutical API Manufacturing",
    excerpt: "Comprehensive guide to FDA regulations for active pharmaceutical ingredient manufacturing, including DMF requirements, GMP compliance, and regulatory submission processes for pharmaceutical suppliers in North America.",
    date: "2024-01-15",
    author: "Dr. Rajesh Kumar",
    category: "Regulatory Compliance",
    slug: "fda-compliance-pharmaceutical-api-manufacturing",
    keywords: ["FDA compliance", "pharmaceutical regulations", "DMF", "GMP", "API manufacturing"]
  },
  {
    title: "Pharmaceutical API Supply Chain: Global Distribution Strategies",
    excerpt: "Explore effective supply chain management for pharmaceutical APIs across North America, Europe, and South America. Learn about logistics, regulatory compliance, and quality assurance in international pharmaceutical trade.",
    date: "2024-01-10",
    author: "Amit Patel",
    category: "Supply Chain",
    slug: "pharmaceutical-api-supply-chain-global-distribution",
    keywords: ["supply chain", "pharmaceutical distribution", "API logistics", "international trade"]
  },
  {
    title: "ISO 9001:2015 Quality Management in Pharmaceutical Manufacturing",
    excerpt: "Deep dive into ISO 9001:2015 requirements for pharmaceutical API manufacturers. Quality control systems, documentation, and continuous improvement processes for regulatory compliance and customer satisfaction.",
    date: "2024-01-05",
    author: "Dr. Priya Sharma",
    category: "Quality Management",
    slug: "iso-9001-quality-management-pharmaceutical",
    keywords: ["ISO 9001", "quality management", "pharmaceutical quality", "quality control systems"]
  },
  {
    title: "Veterinary API Market Trends and Regulatory Requirements 2024",
    excerpt: "Analysis of the growing veterinary pharmaceutical market, including regulatory requirements for veterinary APIs, market opportunities in animal health, and manufacturing compliance for veterinary drug ingredients.",
    date: "2023-12-28",
    author: "Dr. Rajesh Kumar",
    category: "Market Analysis",
    slug: "veterinary-api-market-trends-2024",
    keywords: ["veterinary APIs", "animal health", "veterinary pharmaceuticals", "market trends"]
  }
];

export const SEOBlogSection = () => {
  return (
    <section className="py-16 bg-muted/20" aria-labelledby="blog-section-title">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 id="blog-section-title" className="text-3xl font-bold text-foreground mb-4">
            Pharmaceutical Industry Insights & Expertise
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Expert articles on pharmaceutical API manufacturing, regulatory compliance, quality management, and global pharmaceutical supply chain strategies from industry leaders.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {blogPosts.map((post, index) => (
            <article 
              key={index}
              className="bg-card p-6 rounded-lg border border-border hover:shadow-elevated transition-all group"
              itemScope
              itemType="https://schema.org/Article"
            >
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <time itemProp="datePublished" dateTime={post.date}>
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </time>
                </span>
                <span className="inline-flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span itemProp="author">{post.author}</span>
                </span>
              </div>

              <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full mb-3">
                {post.category}
              </span>

              <h3 
                className="text-xl font-bold text-card-foreground mb-3 group-hover:text-primary transition-colors"
                itemProp="headline"
              >
                {post.title}
              </h3>

              <p className="text-muted-foreground mb-4 leading-relaxed" itemProp="description">
                {post.excerpt}
              </p>

              <div className="flex items-center text-primary font-medium group-hover:gap-3 transition-all">
                <span>Read Article</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>

              {/* Hidden keywords for SEO */}
              <meta itemProp="keywords" content={post.keywords.join(', ')} />
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link 
            to="/news"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark transition-colors font-medium"
          >
            View All Articles
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// FAQ Schema component for better SEO
export const PharmaceuticalFAQ = () => {
  const faqs = [
    {
      question: "What pharmaceutical APIs do you manufacture?",
      answer: "We manufacture pharmaceutical active ingredients across three groups — human APIs, veterinary APIs, and pharmaceutical intermediates. The catalogue covers antibiotics, statins, antiparasitics, and specialised compounds for drug synthesis, all manufactured under ISO 9001:2015 and GMP."
    },
    {
      question: "Are your pharmaceutical APIs FDA approved?",
      answer: "Yes, we maintain FDA compliance with Drug Master File (DMF) submissions for our pharmaceutical APIs. We also hold European CEP certifications and comply with regulatory requirements for Health Canada, ANVISA (Brazil), and COFEPRIS (Mexico), ensuring our products meet international pharmaceutical standards."
    },
    {
      question: "What is your minimum order quantity for APIs?",
      answer: "Minimum order quantities vary by product, typically ranging from 25kg to 100kg for human pharmaceutical APIs. For custom synthesis and specialized compounds, we offer flexible MOQs. Contact our sales team for specific product MOQs and volume pricing for pharmaceutical intermediates and veterinary APIs."
    },
    {
      question: "Do you provide Certificate of Analysis and regulatory documentation?",
      answer: "Yes — every pharmaceutical API shipment ships with the full document pack: Certificate of Analysis (CoA), Safety Data Sheet (SDS), batch manufacturing records, stability data, and the regulatory support docs needed for FDA DMF and European CEP submissions. Traceability and compliance documentation cover the chain end-to-end."
    },
    {
      question: "What quality certifications does your pharmaceutical manufacturing facility have?",
      answer: "Our Pune, Maharashtra manufacturing facility is ISO 9001:2015 certified with GMP compliance. We undergo regular audits by international regulatory authorities and maintain certifications for FDA, EMA, and other global pharmaceutical regulatory bodies, ensuring the highest quality standards for API manufacturing."
    },
    {
      question: "What regions do you supply pharmaceutical APIs to?",
      answer: "We supply pharmaceutical APIs globally to over 50 countries, with strong presence in North America (USA, Canada, Mexico), South America (Brazil, Argentina, Chile), Europe (Germany, France, UK, Italy, Spain), and Asia-Pacific. Our logistics network ensures reliable delivery and regulatory compliance for international pharmaceutical trade."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <section className="py-16 bg-background" aria-labelledby="faq-title">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 id="faq-title" className="text-3xl font-bold text-center text-foreground mb-12">
              Frequently Asked Questions About Pharmaceutical APIs
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-card p-6 rounded-lg border border-border"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3 className="text-lg font-semibold text-card-foreground mb-3" itemProp="name">
                    {faq.question}
                  </h3>
                  <div 
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-muted-foreground leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </>
  );
};
